// client/src/pages/Cart.js

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Build the full media URL from a relative path (like "uploads/123.png").
 */
function getMediaUrl(path) {
  if (!path) return '/assets/images/placeholder.png';
  return `${process.env.REACT_APP_API_URL}/${path}`;
}

export default function Cart() {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    setCartItems(cart);
  }, []);

  // Remove an item from cart
  const removeItem = (id) => {
    const updated = cartItems.filter(item => item.id !== id);
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
    // Dispatch event for Navbar to update cart count
    window.dispatchEvent(new Event('cart-updated'));
  };

  // Handle quantity change (either typed or incremented)
  const handleQuantityChange = (itemId, newQty) => {
    if (newQty < 1) return; // do not allow zero or negative

    const updated = cartItems.map(item => {
      if (item.id === itemId) {
        return { ...item, quantity: newQty };
      }
      return item;
    });
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cart-updated'));
  };

  // Calculate total
  const total = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  if (cartItems.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-4 mt-16">
        <h1 className="text-3xl font-bold mb-4">Your Cart</h1>
        <p className="text-gray-700 mb-4">Your cart is empty.</p>
        <Link to="/shop">
          <button className="bg-primaryGreen text-white px-4 py-2 rounded hover:bg-green-500">
            Return to Shop
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 mt-16 bg-white rounded shadow">
      <h1 className="text-3xl font-bold mb-6">Your Cart</h1>

      {/* Cart Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 font-semibold text-gray-700">Product</th>
              <th className="p-3 font-semibold text-gray-700">Price</th>
              <th className="p-3 font-semibold text-gray-700">Quantity</th>
              <th className="p-3 font-semibold text-gray-700">Subtotal</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {cartItems.map(item => {
              const mediaUrl = getMediaUrl(item.image);
              const itemSubtotal = item.price * item.quantity;

              return (
                <tr key={item.id} className="border-b">
                  {/* Product & Image */}
                  <td className="p-3 flex items-center gap-4">
                    <img
                      src={mediaUrl}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div>
                      <h2 className="font-semibold text-gray-800">
                        {item.name}
                      </h2>
                    </div>
                  </td>

                  {/* Price */}
                  <td className="p-3 text-gray-700">
                    ${item.price}
                  </td>

                  {/* Quantity */}
                  <td className="p-3">
                    <input
                      type="number"
                      min="1"
                      className="w-16 border p-1"
                      value={item.quantity}
                      onChange={e =>
                        handleQuantityChange(item.id, parseInt(e.target.value))
                      }
                    />
                  </td>

                  {/* Subtotal */}
                  <td className="p-3 font-semibold text-gray-800">
                    ${itemSubtotal.toFixed(2)}
                  </td>

                  {/* Remove Button */}
                  <td className="p-3">
                    <button
                      onClick={() => removeItem(item.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cart Summary */}
      <div className="mt-6 flex flex-col items-end">
        <div className="text-lg font-semibold">
          <span className="mr-2">Total:</span>
          <span className="text-primaryGreen">${total.toFixed(2)}</span>
        </div>
        <Link to="/checkout">
          <button className="mt-4 bg-primaryGreen text-white px-6 py-2 rounded hover:bg-green-500">
            Proceed to Checkout
          </button>
        </Link>
      </div>
    </div>
  );
}
